/* global localStorage */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import compose from 'lodash/fp/compose';
import get from 'lodash/get';
import find from 'lodash/find';
import querystring from 'querystring';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withConsumer } from '../../../contexts';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  SolutionBuilderConsumer,
  steps,
} from '../../../contexts/SolutionBuilderContext';
import { Container } from '../../../styles/Grid';
import { getGeoLocationData } from '../../../services/solutionBuilder';
import ProgressBar from './ProgressBar';
import {
  Intro,
  SelectSolutions,
  SelectWorkspaceOptions,
  GeoNarrowing,
} from './Templates';
import * as styles from './index.styles';

const getContextQueryParam = () => {
  const rawParams = window.location.search.substr(1);
  return rawParams ? querystring.parse(rawParams) : {};
};

const getInitialGeoLocationData = async ({ sitecoreContext, manager }) => {
  const { ctx } = getContextQueryParam();

  if (!ctx) return;

  const language = get(sitecoreContext, 'route.itemLanguage', '');
  const { error, data } = await getGeoLocationData(ctx, language);

  if (error) return;

  const parent = find(data, item => item.IsParent);

  if (parent) {
    manager.setStateForStep(steps.geoNarrowing, {
      geoLocationContext: ctx,
    });
  }

  if (!parent.GeoHasChildren) {
    manager.omitGeoNarrowing();
  }
};

const TOTAL_STEPS = 3;

export class SolutionBuilder extends SafePureComponent {
  componentDidMount() {
    const { sitecoreContext, manager } = this.props;
    const language = get(this.props.sitecoreContext, 'route.itemLanguage', '');
    const entryPoint = document.referrer || `/${language.toLowerCase()}`;
    localStorage.setItem('solutionBuilderEntryPoint', entryPoint);

    getInitialGeoLocationData({ sitecoreContext, manager });
  }

  renderTemplate() {
    const { manager, fields } = this.props;
    const { currentStep, steps, getCurrentStepFields } = manager;

    const templates = {
      [steps.intro]: Intro,
      [steps.selectSolutions]: SelectSolutions,
      [steps.selectWorkspaceOptions]: SelectWorkspaceOptions,
      [steps.geoNarrowing]: GeoNarrowing,
    };

    const Component = templates[currentStep];
    const currentStepFields = getCurrentStepFields(fields);

    return <Component key={currentStep} fields={currentStepFields} />;
  }

  renderHelpMessage() {
    const { isHelpMessageVisible, helpText } = this.props.manager;

    return (
      <div
        css={[
          styles.helpMessageContainer,
          isHelpMessageVisible && styles.helpMessageVisible,
        ]}
      >
        <Container>
          <span
            role="alert"
            css={styles.helpMessage}
            aria-hidden={!isHelpMessageVisible}
          >
            {helpText}
          </span>
        </Container>
      </div>
    );
  }

  safeRender() {
    const { manager } = this.props;
    const { isProgressBarVisible } = manager;

    return (
      <div css={styles.mainContainer}>
        {this.renderTemplate()}
        {this.renderHelpMessage()}
        {isProgressBarVisible && (
          <ProgressBar
            step={manager.stepNumber}
            total={TOTAL_STEPS}
            onPrev={manager.goPrev}
            onNext={manager.submitCurrentForm}
            disabled={!manager.isNextEnabled}
          />
        )}
      </div>
    );
  }
}

SolutionBuilder.displayName = 'SolutionBuilder';

SolutionBuilder.defaultProps = {};

SolutionBuilder.propTypes = {};

export default compose(
  withSitecoreContext(),
  withConsumer(SolutionBuilderConsumer, 'manager'),
)(SolutionBuilder);
